/**
 * `/login` and `/register`. An account is optional — it buys saved plans, share
 * links and aircraft profiles; planning and the PDF work without one.
 */
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as api from '../api';
import { AuthLayout } from '../app/AuthLayout';
import { hasKey, useT } from '../i18n';
import { useSession } from '../session';

/**
 * What another page wants said above the form — today only "password changed,
 * please sign in again" after a reset. A catalogue key, not a sentence: the state
 * survives a language switch, and an unknown key is simply not shown.
 */
function noticeKey(state: unknown): string | null {
  const s = state as { notice?: unknown } | null;
  const key = s && typeof s.notice === 'string' ? s.notice : '';
  return key && hasKey(key) ? key : null;
}

export default function Auth({ mode }: { mode: 'login' | 'register' }) {
  const session = useSession();
  const navigate = useNavigate();
  const t = useT();
  const notice = noticeKey(useLocation().state);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  // the raw error is kept, not its text: switching language re-renders the message
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') await session.signIn(email, password);
      else await session.signUp(email, password, displayName);
      navigate('/flights', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  /* The design system, not the predecessor's `card stack primary note`: this is
     the first screen a prospective subscriber sees, and it was the only one
     still wearing the old stylesheet. The tagline above is the page's one
     sentence (§5.5), so the second explaining paragraph is gone. */
  return (
    <AuthLayout title={mode === 'login' ? t('auth.signin') : t('auth.register')} tagline>
      {notice ? <p className="ae-authnote ok">{t(notice)}</p> : null}
      <form className="ae-panel ae-authform" onSubmit={e => void submit(e)}>
        <label className="ae-label">
          {t('auth.email')}
          <input
            className="ae-field"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </label>
        <label className="ae-label">
          {t('auth.password')} {mode === 'register' ? t('auth.password.hint') : ''}
          <input
            className="ae-field"
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </label>
        {mode === 'register' ? (
          <label className="ae-label">
            {t('auth.name')}
            <input
              className="ae-field"
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </label>
        ) : null}
        {error ? <p className="ae-authnote err">{api.apiMessage(t, error)}</p> : null}
        <button type="submit" className="ae-btn ae-btn-primary ae-btn-block" disabled={busy}>
          {busy ? t('auth.busy') : mode === 'login' ? t('auth.signin') : t('auth.register')}
        </button>
      </form>
      {/* the quiet ways out, one row under the button */}
      <div className="ae-authlinks">
        {mode === 'login' ? (
          <>
            <Link to="/register">{t('auth.toRegister')}</Link>
            <Link to="/forgot">{t('pw.forgot.link')}</Link>
          </>
        ) : (
          <Link to="/login">{t('auth.toLogin')}</Link>
        )}
        <Link to="/">{t('nav.noaccount')}</Link>
      </div>
    </AuthLayout>
  );
}
